import { useState, useEffect } from 'react';
import { calculateDose, CalculatorInput } from '../utils/calculatorEngine';
import { SyringeVisualization, SyringeSize } from '../components/calculator/SyringeVisualization';
import { SegmentedControl } from '../components/calculator/SegmentedControl';
import { getStorageItem, setStorageItem } from '../utils/storage';
import { SEO } from '../components/SEO';
import './CalculatorPage.css';

// Persistent storage key for syringe size preference
const SYRINGE_SIZE_KEY = 'calculator:syringeSize';

function CalculatorContent() {
  const [selectedSyringeSize, setSelectedSyringeSize] = useState<SyringeSize>('1.0');

  // Load persisted syringe size preference on mount
  useEffect(() => {
    getStorageItem<SyringeSize>(SYRINGE_SIZE_KEY, '1.0').then((size) => {
      setSelectedSyringeSize(size);
    });
  }, []);

  // Save syringe size preference when it changes
  const handleSyringeSizeChange = (size: SyringeSize) => {
    setSelectedSyringeSize(size);
    setStorageItem(SYRINGE_SIZE_KEY, size).catch((error) => {
      console.warn('[Calculator] Failed to persist syringe size:', error);
    });
  };

  const [vialAmount, setVialAmount] = useState('');
  const [vialUnit, setVialUnit] = useState<'mg' | 'mcg' | 'IU'>('mg');
  const [reconVolume, setReconVolume] = useState('');
  const [desiredDose, setDesiredDose] = useState('');
  const [unit, setUnit] = useState<'mg' | 'mcg' | 'IU'>('mcg');
  const [iuPerMg, setIuPerMg] = useState('');
  const [result, setResult] = useState<ReturnType<typeof calculateDose> | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // Fixed rounding mode at 2 decimal places (0.01)
  const roundingMode = 0.01 as const;

  // Calculate when inputs change
  useEffect(() => {
    if (!vialAmount || !reconVolume || !desiredDose) {
      setResult(null);
      setError(null);
      return;
    }

    // Check if IU conversion is needed
    const needsIuPerMg = (vialUnit === 'IU' || unit === 'IU');
    const iuPerMgValue = iuPerMg ? parseFloat(iuPerMg) : undefined;

    if (needsIuPerMg && !iuPerMgValue) {
      setError('IU/mg conversion factor is required for IU calculations');
      setResult(null);
      return;
    }

    try {
      const input: CalculatorInput = {
        vialAmount,
        vialUnit,
        reconVolumeMl: reconVolume,
        desiredDose,
        desiredDoseUnit: unit,
        iuPerMg: iuPerMgValue,
        roundingMode,
      };

      const calculated = calculateDose(input);
      setResult(calculated);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Calculation error');
      setResult(null);
    }
  }, [vialAmount, vialUnit, reconVolume, desiredDose, unit, iuPerMg]);

  return (
    <>
      <SEO
        title="Peptide Calculator - Peptide Finder"
        description="Calculate precise peptide doses with advanced blending capabilities and unit conversion tools."
        keywords="peptide calculator, dose calculator, peptide reconstitution, injection calculator"
      />
      <div className="calculator-page">
        <header className="calculator-page-header">
          <div className="calculator-page-container">
            <h1>Peptide Calculator</h1>
            <p className="calculator-page-subtitle">
              Calculate precise peptide doses with advanced blending capabilities and unit conversion tools.
            </p>
          </div>
        </header>

        <main className="calculator-page-main">
          <div className="calculator-page-container">
            <div className="calculator-form">
              {/* Vial Amount */}
              <div className="calculator-field">
                <label className="calculator-label">
                  Vial Amount
                </label>
                <div className="calculator-input-group">
                  <input
                    type="number"
                    step="any"
                    value={vialAmount}
                    onChange={(e) => setVialAmount(e.target.value)}
                    placeholder="e.g., 5"
                    className="calculator-input calculator-input-flex"
                  />
                  <select
                    value={vialUnit}
                    onChange={(e) => setVialUnit(e.target.value as 'mg' | 'mcg' | 'IU')}
                    className="calculator-select"
                  >
                    <option value="mg">mg</option>
                    <option value="mcg">mcg</option>
                    <option value="IU">IU</option>
                  </select>
                </div>
              </div>

              {/* IU/mg conversion (only show if needed) */}
              {(vialUnit === 'IU' || unit === 'IU') && (
                <div className="calculator-field">
                  <label className="calculator-label">
                    IU/mg Conversion Factor
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={iuPerMg}
                    onChange={(e) => setIuPerMg(e.target.value)}
                    placeholder="e.g., 3.0"
                    className="calculator-input"
                  />
                  <p className="calculator-hint">
                    Required for IU calculations
                  </p>
                </div>
              )}

              {/* Reconstitution Volume */}
              <div className="calculator-field">
                <label className="calculator-label">
                  Reconstitution Volume (mL)
                </label>
                <input
                  type="number"
                  step="any"
                  value={reconVolume}
                  onChange={(e) => setReconVolume(e.target.value)}
                  placeholder="e.g., 2"
                  className="calculator-input"
                />
              </div>

              {/* Desired Dose */}
              <div className="calculator-field">
                <label className="calculator-label">
                  Desired Dose
                </label>
                <div className="calculator-input-group">
                  <input
                    type="number"
                    step="any"
                    value={desiredDose}
                    onChange={(e) => setDesiredDose(e.target.value)}
                    placeholder="e.g., 250"
                    className="calculator-input calculator-input-flex"
                  />
                  <select
                    value={unit}
                    onChange={(e) => setUnit(e.target.value as 'mg' | 'mcg' | 'IU')}
                    className="calculator-select"
                  >
                    <option value="mg">mg</option>
                    <option value="mcg">mcg</option>
                    <option value="IU">IU</option>
                  </select>
                </div>
              </div>

              {/* Results */}
              {error && (
                <div className="calculator-error">
                  <p>{error}</p>
                </div>
              )}

              <div className="calculator-results">
                <h3 className="calculator-results-title">Calculation Results</h3>
                {result ? (
                  <>
                    <div className="calculator-results-grid">
                      <div className="calculator-result-item">
                        <div className="calculator-result-label">Volume to Draw</div>
                        <div className="calculator-result-value">
                          {result.display.volumeToDraw}
                        </div>
                      </div>
                      <div className="calculator-result-item">
                        <div className="calculator-result-label">Concentration</div>
                        <div className="calculator-result-value">
                          {result.display.concentration}
                        </div>
                      </div>
                      <div className="calculator-result-item">
                        <div className="calculator-result-label">Dose per Injection</div>
                        <div className="calculator-result-value">
                          {result.display.dosePerInjection}
                        </div>
                      </div>
                      <div className="calculator-result-item">
                        <div className="calculator-result-label">Total Injections</div>
                        <div className="calculator-result-value">
                          {result.display.totalInjections}
                        </div>
                      </div>
                    </div>
                    {result.display.iu && (
                      <div className="calculator-result-iu">
                        <div className="calculator-result-label">IU Equivalent</div>
                        <div className="calculator-result-value">{result.display.iu}</div>
                      </div>
                    )}
                    {result.display.roundingNote && (
                      <div className="calculator-rounding-note">{result.display.roundingNote}</div>
                    )}
                  </>
                ) : (
                  <div className="calculator-results-empty">
                    <p>Enter values above to see calculation results</p>
                  </div>
                )}
              </div>

              {/* Syringe visualization */}
              <div className="calculator-syringe-section">
                <label className="calculator-label">
                  Syringe Size
                </label>
                <SegmentedControl<SyringeSize>
                  options={[
                    { label: '0.3 mL', value: '0.3' },
                    { label: '0.5 mL', value: '0.5' },
                    { label: '1.0 mL', value: '1.0' },
                  ]}
                  value={selectedSyringeSize}
                  onValueChange={handleSyringeSizeChange}
                  textVariant="caption"
                />
                <div className="calculator-syringe-container">
                  <SyringeVisualization
                    injectionVolumeMl={result?.normalized.mlPerInjection?.toNumber() || 0}
                    syringeSize={selectedSyringeSize}
                  />
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export function CalculatorPage() {
  return <CalculatorContent />;
}
